import React, { useState, useEffect, useCallback } from 'react';
import FilterPill from '../FilterPill/FilterPill';
import './LanguageSelector.css';

interface LanguageSelectorProps {
  onChange?: (activeLanguages: string[]) => void;
  initialLanguages?: string[];
}

const defaultLanguages = [
  'English',
  'Spanish',
  'French',
  'German',
  'Portuguese',
  'Japanese',
   'S. Chinese',
  'T. Chinese',
  'Korean', 
];

// Map language codes to display names
const languageCodeMap: Record<string, string> = {
  'EN': 'English',
  'ES': 'Spanish',
  'FR': 'French',
  'DE': 'German',
  'PT': 'Portuguese',
  'JA': 'Japanese',
  'ZH-HANS': 'S. Chinese',
  'ZH-HANT': 'T. Chinese',
  'KO': 'Korean'
};

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  onChange,
  initialLanguages
}) => {
  const getInitialState = useCallback(() => {
    return defaultLanguages.reduce((acc, lang) => {
      // If initialLanguages is provided, only activate those languages
      // Otherwise, activate all languages (default behavior)
      let isActive = true;
      if (initialLanguages) {
        // Check if the language or its code is in the initial languages
        const langCode = Object.keys(languageCodeMap).find(code => languageCodeMap[code] === lang);
        // Check for both uppercase and lowercase language codes
        isActive = initialLanguages.includes(lang) ||
                  (langCode ? initialLanguages.includes(langCode) : false) ||
                  (langCode ? initialLanguages.includes(langCode.toLowerCase()) : false) ||
                  // Special case for 'en' -> 'English'
                  (lang === 'English' && initialLanguages.includes('en'));
      }
      return { ...acc, [lang]: isActive };
    }, {} as Record<string, boolean>);
  }, [initialLanguages]);

  const [active, setActive] = useState<Record<string, boolean>>(getInitialState());
  const [isExpanded, setIsExpanded] = useState(false);

  // Update state when props change
  useEffect(() => {
    setActive(getInitialState());
  }, [getInitialState]);

  const toggle = (lang: string) => {
    const updated = { ...active, [lang]: !active[lang] };
    setActive(updated);
    onChange?.(Object.keys(updated).filter((k) => updated[k]));
  };

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  // Get selected languages
  const selectedLanguages = defaultLanguages.filter(lang => active[lang]);

  // Determine which languages to display
  // If no languages selected, default to showing English
  const displayLanguages = isExpanded
    ? defaultLanguages
    : (selectedLanguages.length > 0 ? selectedLanguages : ['English']);

  // Count hidden languages
  const hiddenCount = defaultLanguages.length - displayLanguages.length;

  return (
    <div className="language-selector">
      <label>Language:</label>
      <div className="language-selector-buttons">
        {displayLanguages.map((lang) => (
          <FilterPill
            key={lang}
            label={lang}
            active={active[lang]}
            onToggle={() => toggle(lang)}
          />
        ))}
        {!isExpanded && hiddenCount > 0 && (
          <button
            className="language-more-toggle"
            onClick={toggleExpanded}
          >
            + {hiddenCount} more
          </button>
        )}
        {isExpanded && (
          <button
            className="language-more-toggle"
            onClick={toggleExpanded}
          >
            Show less
          </button>
        )}
      </div>
    </div>
  );
};

export default LanguageSelector;
